// Предпросмотр страниц «как напечатает Word»: панель «Страницы» справа (миниатюры PDF с сервера) и просмотр
// страницы крупно (стрелки - листать). Сборка PDF - через writer и Word (как «Экспорт в PDF»).
import { useEffect, useState } from 'react';
import type { RegistryApi, Ctx, DialogProps } from '../../core/registry';
import { saveNow } from '../../core/project';
import { appStore, useApp } from '../../core/store';
import { uiStore, useUi } from '../../ui/uiState';
import { Btn, Empty, Modal } from '../../ui/controls';
import { Icon } from '../../ui/icons';

let building = false;

export async function buildPreview(ctx: Ctx) {
  const pid = ctx.store.getState().projectId;
  if (!pid || building) return;
  building = true;
  uiStore.getState().set({ task: { label: 'Предпросмотр', progress: 'Сборка PDF…' } });
  try {
    await saveNow();
    const rev = ctx.store.getState().revision;
    const { job } = await ctx.api.build(pid, { pdf: true, check: false });
    const res = await ctx.api.waitJob(job, (s) => uiStore.getState().set({ task: { label: 'Предпросмотр', progress: s.progress } }));
    if (!res.pdf) { ctx.toast('PDF не создан: для предпросмотра нужен Microsoft Word', 'warn', 6000); return; }
    const pages = res.pages || (await ctx.api.previewInfo(pid)).pages;
    uiStore.getState().set({ built: { pages, revision: rev, at: Date.now(), pdf: res.pdf } });
  } catch (e: any) {
    ctx.toast(e?.code === 'cancelled' ? e.message : 'Предпросмотр: ' + e.message, e?.code === 'cancelled' ? 'info' : 'error', 6000);
  } finally {
    building = false;
    uiStore.getState().set({ task: null });
  }
}

function PagesPanel({ ctx }: { ctx: Ctx }) {
  const pid = useApp((s) => s.projectId)!;
  const rev = useApp((s) => s.revision);
  const built = useUi((s) => s.built);
  const task = useUi((s) => s.task);
  const [pages, setPages] = useState<number>(built?.pages || 0);
  useEffect(() => {
    if (built?.pages) { setPages(built.pages); return; }
    ctx.api.previewInfo(pid).then((r) => {
      setPages(r.pages);
      if (r.pages && !uiStore.getState().built) uiStore.getState().set({ built: { pages: r.pages, revision: -1, at: 0, pdf: null } });
    }).catch(() => setPages(0));
  }, [built, pid]);
  const stale = !!built && built.revision >= 0 && built.revision !== rev;
  const stamp = String(built?.at || 0);
  return (
    <div className="pages-panel" data-testid="pages-panel">
      <div className="panel-tools">
        <Btn icon="refresh" label={pages ? 'Обновить' : 'Собрать PDF'} primary={!pages || !!stale} disabled={!!task}
          onClick={() => buildPreview(ctx)} testid="pages-build" />
        {pages > 0 && <span className="muted">{pages} стр.{stale ? ' · текст изменён' : ''}</span>}
      </div>
      {task?.label === 'Предпросмотр' && <div className="muted pad"><Icon name="spinner" className="spin" /> {task.progress}</div>}
      {!pages && !task && (
        <Empty icon="eye" title="Страницы ещё не собраны" text="Нажмите «Собрать PDF» - Word сверстает отчёт, и здесь будут страницы точно как при печати." />
      )}
      <div className="pages-list">
        {Array.from({ length: pages }, (_, i) => (
          <button key={i} type="button" className="page-thumb" onClick={() => ctx.openDialog('shell.page', { n: i + 1, pages, stamp })}
            data-testid="page-thumb" aria-label={`Страница ${i + 1}`}>
            <img src={ctx.api.previewPageUrl(pid, i + 1, 50, stamp)} alt="" loading="lazy" />
            <span>{i + 1}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PageDialog({ ctx, close, props }: DialogProps) {
  const pid = appStore.getState().projectId!;
  const [n, setN] = useState<number>(props.n);
  const total: number = props.pages;
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown') setN((x) => Math.min(total, x + 1));
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') setN((x) => Math.max(1, x - 1));
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [total]);
  return (
    <Modal title={`Страница ${n} из ${total}`} onClose={() => close()} width={820} testid="page-dialog" footer={<>
      <Btn icon="chevron-left" label="Назад" disabled={n <= 1} onClick={() => setN(n - 1)} />
      <Btn icon="chevron-right" label="Вперёд" disabled={n >= total} onClick={() => setN(n + 1)} />
      <span className="topbar-spacer" />
      <Btn label="Закрыть" onClick={() => close()} />
    </>}>
      <div className="page-big"><img src={ctx.api.previewPageUrl(pid, n, 110, props.stamp)} alt={`Страница ${n}`} /></div>
    </Modal>
  );
}

export function setupPreview(r: RegistryApi) {
  r.panels.add({ id: 'pages', title: 'Страницы', icon: 'eye', side: 'right', order: 40, component: PagesPanel,
    badge: () => { const b = uiStore.getState().built; return b?.pages || null; } });
  r.dialogs.add({ id: 'shell.page', component: PageDialog });
  r.commands.add({ id: 'view.preview', title: 'Предпросмотр страниц', description: 'Страницы так, как их сверстает Word (PDF)', icon: 'eye',
    run: (c) => {
      const p = appStore.getState().panels;
      appStore.getState().set({ panels: { ...p, right: 'pages' } });
      const b = uiStore.getState().built;
      if (!b || b.revision !== c.store.getState().revision) void buildPreview(c);
    } });
  r.ribbonItems.add({ id: 'preview', group: 'view.pages', type: 'large', command: 'view.preview', label: 'Предпросмотр' });
  r.events.on('check:done', (res: any) => {
    if (res?.pdf) uiStore.getState().set({ built: { pages: res.pages, revision: appStore.getState().revision, at: Date.now(), pdf: res.pdf } });
  });
  r.events.on('project:opened', () => uiStore.getState().set({ built: null }));
}
